import { useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import toast from "react-hot-toast";

const WhatsAppShareModal = ({ isOpen, onClose, pdfUrl, filename, phone, accountName, startDate, endDate }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  // Initialize phone number and message when modal opens
  useEffect(() => {
    if (isOpen) {
      // Autofill phone number from prop (editable)
      if (phone) {
        setPhoneNumber(phone);
      } else {
        setPhoneNumber("");
      }

      // Set default message without PDF link (link will be appended when sending)
      if (startDate && endDate) {
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        const accountText = accountName ? `This is your account ${accountName} from ${formattedStartDate} to ${formattedEndDate}` : `This is your account from ${formattedStartDate} to ${formattedEndDate}`;
        setMessage(accountText);
      } else {
        const accountText = accountName ? `This is your account ${accountName}` : "This is your account statement";
        setMessage(accountText);
      }
    }
  }, [isOpen, phone, startDate, endDate]);

  if (!isOpen) return null;

  // Normalize phone number (remove spaces, +, and ensure it's digits only)
  const normalizePhoneNumber = (phone) => {
    // Remove all non-digit characters except keep the number
    let normalized = phone.replace(/\D/g, "");

    // If it starts with 0, remove it
    if (normalized.startsWith("0")) {
      normalized = normalized.substring(1);
    }

    return normalized;
  };

  // Validate phone number
  const validatePhoneNumber = (phone) => {
    const normalized = normalizePhoneNumber(phone);
    // Should be between 10-15 digits (international format)
    return normalized.length >= 10 && normalized.length <= 15;
  };

  const handleOpenWhatsApp = () => {
    if (!phoneNumber.trim()) {
      toast.error("Please enter a WhatsApp number");
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phoneNumber);

    if (!validatePhoneNumber(normalizedPhone)) {
      toast.error("Please enter a valid phone number (10-15 digits)");
      return;
    }

    // Build final message: user message + new line + PDF link
    let finalMessage = message || "This is your account statement";

    // Append PDF link after one line if PDF URL exists
    if (pdfUrl) {
      finalMessage = `${finalMessage}\n\n${pdfUrl}`;
    }

    // Encode message for URL
    const encodedMessage = encodeURIComponent(finalMessage);

    // Generate WhatsApp share link
    const whatsappUrl = `https://wa.me/${normalizedPhone}?text=${encodedMessage}`;

    // Open WhatsApp in new tab/window
    window.open(whatsappUrl, "_blank");

    toast.success("Opening WhatsApp...");
    onClose();
  };

  const handleClose = () => {
    setPhoneNumber("");
    setMessage("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FaWhatsapp className="text-green-500 text-2xl" />
            <h2 className="text-xl font-semibold">Share via WhatsApp</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <ImCross />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Phone Number Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g., +919876543210 or 919876543210"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Include country code (e.g., 91 for India)
            </p>
          </div>

          {/* Message Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            {pdfUrl && (
              <p className="mt-1 text-xs text-gray-500">
                PDF link will be automatically added at the end of the message
              </p>
            )}
          </div>

          {/* PDF Info */}
          {pdfUrl && (
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-600 mb-1">
                <strong>File:</strong> {filename || "Ledger PDF"}
              </p>
              <p className="text-xs text-gray-500 break-all">
                {pdfUrl}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleOpenWhatsApp}
            disabled={!phoneNumber.trim()}
            className="px-4 py-2 text-white bg-green-500 rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FaWhatsapp />
            Open WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppShareModal;

